<script setup lang="ts">
    import { useConfigStore } from '@/store/config.store';
    import BaseButton from '@/components/BaseButton.vue';
    import BaseIcon from '@/components/BaseIcon.vue';
    import Icon3dSymbol from '@/components/icons/Icon3dSymbol.vue';
    import IconGrid from '@/components/icons/IconGrid.vue';
    import IconMeasurement from '@/components/icons/IconMeasurement.vue';
    import MobileTextEditForm from '@/components/MobileTextEditForm.vue';
    import MobilePopup from '@/components/MobilePopUp.vue';
    import TextFormContent from '@/components/TextFormContent.vue';
    import { EAppStep } from '@/typescript/enums/EAppStep';
    import { useTemplateStore } from '@/store/template.store';
    import { useGraduation } from '@/composables/useGraduation';
    import { useGrid } from '@/composables/useGrid';
    import { useProductHas3d } from '@/composables/useProductHas3d';
    import FormatSelection from '@/components/FormatSelection.vue';
    import { EProductFormat } from '@/typescript/enums/EProductFormat';
    import { storeToRefs } from 'pinia';
    import { computed, ref } from 'vue';
    import type { ITextField } from '@/typescript/interfaces/ITextField';
    import type { TSides } from '@/typescript/interfaces/TSide';

    defineEmits<{
        toggleRectoVerso: [view: TSides];
    }>();
    const { selectedField } = storeToRefs(useTemplateStore());
    const { hasGraduation, toggleGraduation } = useGraduation();
    const { displayGrid, toggleGrid } = useGrid();
    const configStore = useConfigStore();
    const { isCup, productFormat } = storeToRefs(configStore);

    const goToPreviewStep = () => {
        configStore.currentStep = EAppStep.PREVIEW;
    };

    const isTextFormVisible = computed(() => selectedField.value && 'defaultText' in selectedField.value);
    const showTextPopup = ref(false);
    const { productHas3DModel } = useProductHas3d();
</script>

<template>
    <transition
        enter-active-class="animate-slide-in-bottom "
        leave-active-class="animate-slide-out-bottom"
    >
        <MobileTextEditForm
            v-if="isTextFormVisible"
            :field="selectedField as ITextField"
        />
        <div
            v-else
            class="absolute bottom-5 z-10 flex h-20 w-full flex-row items-center justify-center"
        >
            <div class="z-10 flex h-full flex-row items-center justify-center gap-6 bg-bg-secondary px-4 shadow-mobile-bar">
                <BaseButton
                    class="h-14 w-14"
                    disable-hover
                    @click="showTextPopup = true"
                >
                    <span class="sr-only">
                        {{ $t('add_field') }}
                    </span>
                    <BaseIcon
                        class="h-6 w-6"
                        name="PlusIcon"
                    />
                </BaseButton>
                <BaseButton
                    disable-hover
                    :variant="displayGrid ? 'filled' : 'outlined'"
                    class="h-14 w-14"
                    @click="toggleGrid"
                >
                    <span class="sr-only">
                        {{ $t('help_grid') }}
                    </span>
                    <IconGrid class="h-6 w-6" />
                </BaseButton>
                <BaseButton
                    v-if="productHas3DModel && isCup"
                    disable-hover
                    class="h-14 w-14"
                    :variant="hasGraduation ? 'filled' : 'outlined'"
                    @click="toggleGraduation"
                >
                    <span class="sr-only">
                        {{ $t('graduation') }}
                    </span>
                    <IconMeasurement />
                </BaseButton>
                <BaseButton
                    v-if="productHas3DModel"
                    disable-hover
                    class="h-14 w-14 flex-col"
                    @click="goToPreviewStep"
                >
                    <span class="sr-only">
                        {{ $t('show_preview') }}
                    </span>
                    <Icon3dSymbol class="h-6 w-6" />
                    <span class="font-input-serif text-sm font-medium">3D</span>
                </BaseButton>
                <FormatSelection v-if="productFormat === EProductFormat.RECTO_VERSO" />
            </div>
            <MobilePopup
                :show="showTextPopup"
                @close="showTextPopup = false"
            >
                <TextFormContent @close="showTextPopup = false" />
            </MobilePopup>
        </div>
    </transition>
</template>
